import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  createContent,
  getAllContents,
  getAllContentsLegacy,
  getContentById,
  updateContent,
  deleteContent,
  addLike,
  removeLike,
  addComment,
} from "./contentCrudService";
import { formatErrorMessage, logError } from "../../utils/errorHandler";
import { pushActivity, ACTIVITY_TYPES } from "../../services/activityLogService";
import {
  notifyFollowersNewContent,
  notifyLike,
  notifyComment,
} from "../../services/notificationService";
import { typeToExploreSlug, CONTENT_STATUS, UNIFIED_CATALOG_SLUG } from "./contentConstants";
import { showAppToast } from "../../utils/appToastBus";
import { evaluateWriterPublishTransition } from "../../services/writerUsageService";
import { ROLE, resolveRoleKey } from "../../auth/roleUtils";

const formatContentError = (error) =>
  formatErrorMessage(error, "Something went wrong with this content action. Please try again.");

const CATALOG_CACHE_TTL_MS = 60 * 1000;

export const fetchContentsThunk = createAsyncThunk(
  "content/fetchAll",
  async (_, { getState, rejectWithValue }) => {
    try {
      const user = getState()?.auth?.user;
      const role = resolveRoleKey(user);
      if (role === ROLE.SUPERADMIN) return await getAllContentsLegacy({ scope: "admin" });
      if (role === ROLE.WRITER && user?.uid) return await getAllContentsLegacy({ scope: "writer", uid: user.uid });
      return await getAllContentsLegacy({ scope: "public" });
    } catch (error) {
      logError(error, "Fetch Contents");
      return rejectWithValue(formatContentError(error));
    }
  },
  {
    condition: (arg, { getState }) => {
      const state = getState();
      const force = !!arg?.force;
      if (force) return true;

      const loading = !!state?.content?.loading;
      if (loading) return false;

      const lastFetchedAt = Number(state?.content?.lastFetchedAt || 0);
      if (!lastFetchedAt) return true;

      return Date.now() - lastFetchedAt > CATALOG_CACHE_TTL_MS;
    },
  }
);

/** Paginated browse list (Reader/Writer content lists) — does not replace full `items` catalog. */
export const fetchBrowseContentsPaginatedThunk = createAsyncThunk(
  "content/fetchBrowsePaginated",
  async (options = {}, { getState, rejectWithValue }) => {
    try {
      const { limit = 24, type = null, lastKey = null, lastCreatedAt = null } = options || {};
      return await getAllContents({
        limit,
        lastKey,
        lastCreatedAt,
        orderBy: "createdAt",
        type,
      });
    } catch (error) {
      logError(error, "Fetch Browse Contents Paginated");
      return rejectWithValue(formatContentError(error));
    }
  },
  {
    condition: (arg, { getState }) => {
      const c = getState().content;
      if (c?.browse?.loading || c?.browse?.loadingMore) return false;
      return true;
    },
  }
);

export const fetchContentByIdThunk = createAsyncThunk(
  "content/fetchById",
  async (contentId, { rejectWithValue }) => {
    try {
      return await getContentById(contentId);
    } catch (error) {
      logError(error, "Fetch Content By ID");
      return rejectWithValue(formatContentError(error));
    }
  }
);

export const createContentThunk = createAsyncThunk(
  "content/create",
  async (contentData, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const user = state.auth?.user;
      const creatorInfo = user
        ? {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            authorName: user.authorName || "",
          }
        : null;

      if (contentData.status === CONTENT_STATUS.PUBLISHED) {
        if (!creatorInfo?.uid) {
          const msg = "You must be signed in to publish content.";
          showAppToast({ severity: "warn", summary: "Sign in required", detail: msg });
          return rejectWithValue(msg);
        }
        const gate = await evaluateWriterPublishTransition({
          user,
          previousItem: null,
          nextPayload: contentData,
        });
        if (!gate.ok) {
          showAppToast({ severity: "warn", summary: "Cannot publish", detail: gate.reason });
          return rejectWithValue(gate.reason);
        }
      }

      let payloadForCreate = contentData;
      if (creatorInfo?.uid && contentData.status === CONTENT_STATUS.PUBLISHED) {
        const catalog = state.content?.items || [];
        const publishedMine = catalog.filter(
          (x) =>
            (x.createdBy === creatorInfo.uid || x.authorId === creatorInfo.uid) &&
            String(x.status || "").toLowerCase() === CONTENT_STATUS.PUBLISHED
        );
        if (publishedMine.length < 3) {
          payloadForCreate = { ...contentData, isBoosted: true };
        }
      }

      const contentId = await createContent(payloadForCreate, creatorInfo);
      const created = await getContentById(contentId);

      if (creatorInfo) {
        const slug = typeToExploreSlug(created.type);
        try {
          await notifyFollowersNewContent(
            creatorInfo.uid,
            creatorInfo.displayName ||
              creatorInfo.authorName ||
              creatorInfo.email ||
              "A writer",
            slug,
            contentId,
            created.title
          );
        } catch (e) {
          console.error("Content create notify followers failed", e);
        }
        pushActivity({
          type: ACTIVITY_TYPES.CONTENT_CREATED,
          userId: creatorInfo.uid,
          userEmail: creatorInfo.email,
          userName: creatorInfo.displayName,
          contentType: slug,
          contentId,
          title: created.title,
        }).catch(() => {});
      }

      return created;
    } catch (error) {
      logError(error, "Create Content");
      return rejectWithValue(formatContentError(error));
    }
  }
);

export const updateContentThunk = createAsyncThunk(
  "content/update",
  async ({ contentId, updates }, { rejectWithValue, getState }) => {
    try {
      const existing = await getContentById(contentId);
      const user = getState().auth?.user;
      const merged = { ...existing, ...updates };

      if (merged.status === CONTENT_STATUS.PUBLISHED) {
        if (!user?.uid) {
          const msg = "You must be signed in to publish content.";
          showAppToast({ severity: "warn", summary: "Sign in required", detail: msg });
          return rejectWithValue(msg);
        }
        const gate = await evaluateWriterPublishTransition({
          user,
          previousItem: existing,
          nextPayload: merged,
        });
        if (!gate.ok) {
          showAppToast({ severity: "warn", summary: "Cannot publish", detail: gate.reason });
          return rejectWithValue(gate.reason);
        }
      }

      await updateContent(contentId, updates);
      const updated = await getContentById(contentId);

      if (user) {
        pushActivity({
          type: ACTIVITY_TYPES.CONTENT_UPDATED,
          userId: user.uid,
          userEmail: user.email,
          userName: user.displayName,
          contentType: typeToExploreSlug(updated.type),
          contentId,
          title: updated.title || updates?.title,
        }).catch(() => {});
      }
      return updated;
    } catch (error) {
      logError(error, "Update Content");
      return rejectWithValue(formatContentError(error));
    }
  }
);

export const deleteContentThunk = createAsyncThunk(
  "content/delete",
  async (contentId, { rejectWithValue, getState }) => {
    try {
      const existing = await getContentById(contentId);
      await deleteContent(contentId);
      const user = getState().auth?.user;
      if (user) {
        pushActivity({
          type: ACTIVITY_TYPES.CONTENT_DELETED,
          userId: user.uid,
          userEmail: user.email,
          userName: user.displayName,
          contentType: typeToExploreSlug(existing.type),
          contentId,
          title: existing.title || contentId,
        }).catch(() => {});
      }
      return contentId;
    } catch (error) {
      logError(error, "Delete Content");
      return rejectWithValue(formatContentError(error));
    }
  }
);

export const addLikeThunk = createAsyncThunk(
  "content/addLike",
  async ({ contentId, userName }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const user = state.auth?.user;
      const likeId = user?.uid;
      if (!likeId) return rejectWithValue("Sign in to like this piece.");
      await addLike(contentId, likeId);
      const item =
        state.content?.items?.find((p) => p.id === contentId) ||
        state.content?.currentItem;
      if (item && user && item.createdBy && item.createdBy !== user.uid) {
        try {
          await notifyLike(
            item.createdBy,
            contentId,
            typeToExploreSlug(item.type),
            userName || user.displayName || user.email,
            user.uid
          );
        } catch (e) {
          console.error("notifyLike failed", e);
        }
      }
      if (user) {
        pushActivity({
          type: ACTIVITY_TYPES.LIKE_ADDED,
          userId: user.uid,
          userEmail: user.email,
          userName: userName || user.displayName,
          contentType: item ? typeToExploreSlug(item.type) : UNIFIED_CATALOG_SLUG,
          contentId,
        }).catch(() => {});
      }
      return { contentId, userId: likeId, userName };
    } catch (error) {
      return rejectWithValue(formatContentError(error));
    }
  }
);

export const removeLikeThunk = createAsyncThunk(
  "content/removeLike",
  async ({ contentId, userName }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const user = state.auth?.user;
      const likeId = user?.uid;
      if (!likeId) return rejectWithValue("Sign in to remove this like.");
      await removeLike(contentId, likeId);
      const item =
        state.content?.items?.find((p) => p.id === contentId) ||
        state.content?.currentItem;
      if (user) {
        pushActivity({
          type: ACTIVITY_TYPES.LIKE_REMOVED,
          userId: user.uid,
          userEmail: user.email,
          userName: userName || user.displayName,
          contentType: item ? typeToExploreSlug(item.type) : UNIFIED_CATALOG_SLUG,
          contentId,
        }).catch(() => {});
      }
      return { contentId, userId: likeId, userName };
    } catch (error) {
      return rejectWithValue(formatContentError(error));
    }
  }
);

export const addCommentThunk = createAsyncThunk(
  "content/addComment",
  async ({ contentId, userName, commentText }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const user = state.auth?.user;
      if (!user?.uid) {
        return rejectWithValue("You need to be signed in to comment.");
      }
      const commentId = await addComment(contentId, userName, commentText, user.uid);
      const updated = await getContentById(contentId);
      if (updated && user && updated.createdBy && updated.createdBy !== user.uid) {
        try {
          await notifyComment(
            updated.createdBy,
            contentId,
            typeToExploreSlug(updated.type),
            userName || user.displayName || user.email,
            user.uid
          );
        } catch (e) {
          console.error("notifyComment failed", e);
        }
      }
      if (user && updated) {
        pushActivity({
          type: ACTIVITY_TYPES.COMMENT_ADDED,
          userId: user.uid,
          userEmail: user.email,
          userName: userName || user.displayName,
          contentType: typeToExploreSlug(updated.type),
          contentId,
          commentText: commentText?.slice(0, 200),
        }).catch(() => {});
      }
      return { contentId, commentId, ...(updated?.comments?.[commentId] || {}) };
    } catch (error) {
      return rejectWithValue(formatContentError(error));
    }
  }
);
